import React, { useEffect, useMemo, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin, { DateClickArg } from "@fullcalendar/interaction";
import { EventApi, EventContentArg, EventInput } from "@fullcalendar/core";
import moment from "moment";
import "./calendar-custom.css";
import { motion, AnimatePresence } from "framer-motion";
import {
  Modal,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  IconButton,
  Typography,
  Chip,
  Autocomplete,
  SelectChangeEvent,
} from "@mui/material";
import {
  addEventToGroupService,
  deleteEventGroupService,
  getEventGroupService,
  updateEventGroupService,
} from "../../../services/event-service";
import {
  getGroupByIdService,
  getUserGroupService,
} from "../../../services/group-service";
import { ArrowLeft, ArrowRight, Add, Today, CalendarMonth, AccessTime, Link } from "@mui/icons-material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTimes, faCalendarAlt, faLink, faClock, faBook, faUsers, faCheck } from "@fortawesome/free-solid-svg-icons";
import { RootState } from "../../../redux/store/store";
import { useSelector } from "react-redux";

type GroupOption = { label: string; value: number };
type SubjectOption = { id: number; label: string };

type CalendarEvent = EventInput & {
  id: string;
  title: string;
  start: string;
  end: string;
  backgroundColor: string;
  discordUrl?: string;
  classId: number;
  classTitle: string;
  subjectId?: number | null;
  subjectTitle?: string;
  subjects?: string[];
};

type EventFormState = {
  id: string | null;
  title: string;
  start: string;
  end: string;
  backgroundColor: string;
  classId: number | null;
  subjectId: number | null;
  discordUrl: string;
};

const colorOptions = [
  { hex: "#0369A1", rgba: "rgba(3, 105, 161, 0.3)" },
  { hex: "#FEBA0C", rgba: "rgba(254, 186, 12, 0.3)" },
  { hex: "#00DF76", rgba: "rgba(0, 223, 118, 0.3)" },
  { hex: "#9C4DF4", rgba: "rgba(156, 77, 244, 0.3)" },
  { hex: "#F8BAD4", rgba: "rgba(248, 186, 212, 0.3)" },
  { hex: "#CC6144", rgba: "rgba(204, 97, 68, 0.3)" },
];

type HeaderProps = { 
  currentDate: string; 
  onPrev: () => void; 
  onNext: () => void; 
  onToday: () => void; 
  onWeekView: () => void; 
  onMonthView: () => void; 
  activeView: string; 
  onOpenModal: () => void; 
  groupOptions: GroupOption[]; 
  selectedGroups: GroupOption[]; 
  onGroupsChange: (v: GroupOption[]) => void; 
  subjectTags: string[]; 
  canAddEvents: boolean; 
  loading: boolean; 
  totalEvents: number; 
};

const Header: React.FC<HeaderProps> = ({ 
  currentDate, onPrev, onNext, onToday, onWeekView, onMonthView, 
  activeView, onOpenModal, groupOptions, selectedGroups, 
  onGroupsChange, subjectTags, canAddEvents, loading, totalEvents 
}) => {
  return (
    <motion.div 
      initial={{ y: -50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="relative overflow-hidden text-white shadow-[0_40px_100px_-20px_rgba(5,50,43,0.3)] rounded-[2.5rem] bg-[#05322B] group border border-white/10"
    >
      <div className="absolute inset-0 opacity-40 pointer-events-none overflow-hidden">
        <div className="absolute top-[-50%] left-[-10%] w-[80%] h-[150%] bg-[#0af0b4] blur-[150px] rounded-full mix-blend-screen opacity-20 animate-pulse" />
        <div className="absolute bottom-[-50%] right-[-10%] w-[60%] h-[120%] bg-primary blur-[120px] rounded-full mix-blend-screen opacity-30" />
      </div>
      
      <div className="relative z-10 p-6 lg:p-10 space-y-6">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-3xl border border-white/20 shadow-2xl">
               <span className="w-2 h-2 rounded-full bg-[#0af0b4] animate-ping" />
               <span className="text-[10px] font-black uppercase tracking-[0.4em]">{currentDate}</span>
            </div>
            <h2 className="text-3xl lg:text-[48px] font-black leading-[0.85] tracking-tighter drop-shadow-2xl flex flex-col">
               <span className="opacity-90">Votre</span>
               <span className="text-emerald-400 italic -mt-1">Planning</span>
            </h2>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex bg-black/20 backdrop-blur-3xl border border-white/10 p-2 rounded-[2rem] shadow-3xl">
              <IconButton className="!text-white hover:!bg-white/10 !transition-all !w-14 !h-14 !rounded-2xl" onClick={onPrev}><ArrowLeft /></IconButton>
              <Button onClick={onToday} className="!text-white !font-black !px-8 !text-[10px] !uppercase !tracking-[0.3em] hover:!bg-white/10 !transition-all">Calendrier local</Button>
              <IconButton className="!text-white hover:!bg-white/10 !transition-all !w-14 !h-14 !rounded-2xl" onClick={onNext}><ArrowRight /></IconButton>
            </div>
            {canAddEvents && (
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onOpenModal} 
                className="flex items-center gap-4 px-8 py-4 bg-white text-[#05322B] rounded-[2rem] font-black text-[12px] uppercase tracking-[0.2em] shadow-[0_20px_50px_rgba(255,255,255,0.2)] hover:bg-emerald-400 transition-all duration-500"
              >
                <Add fontSize="medium" />
                <span>Nouveau Cours</span>
              </motion.button>
            )}
          </div>
        </div>

        <div className="w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-6">
          <div className="flex-1 max-w-4xl space-y-4">
            <div className="flex items-center gap-4">
               <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center border border-white/10 backdrop-blur-xl shadow-xl">
                  <CalendarMonth fontSize="small" className="text-emerald-400" />
               </div>
               <div>
                  <h4 className="text-[10px] font-black uppercase tracking-[0.5em] text-emerald-400">Canaux de Promotion</h4>
                  <p className="text-[9px] font-bold text-white/40 uppercase tracking-widest mt-1">Filtres dynamiques par groupes</p>
               </div>
            </div>
            <Autocomplete multiple options={groupOptions} getOptionLabel={(o) => o.label} value={selectedGroups} onChange={(_, v) => onGroupsChange(v)} renderTags={(v, g) => v.map((o, index) => <Chip {...g({ index })} key={o.value} label={o.label} sx={{ bgcolor: 'white !important', color: '#05322B !important', fontWeight: 900, textTransform: 'uppercase', fontSize: 9, borderRadius: 2, height: 32, px: 1.5, boxShadow: '0 10px 20px rgba(0,0,0,0.2)' }} />)} renderInput={(p) => <TextField {...p} placeholder="Rechercher une promotion..." />} sx={{ "& .MuiInputBase-root": { bgcolor: "rgba(255,255,255,0.05)", backdropFilter: "blur(50px)", borderRadius: "1.5rem", border: '1px solid rgba(255,255,255,0.1)', minHeight: 60, transition: "0.4s", "&:hover": { bgcolor: "rgba(255,255,255,0.08)" } }, "& .MuiOutlinedInput-notchedOutline": { border: 'none' }, "& .MuiAutocomplete-endAdornment .MuiIconButton-root": { color: 'white' } }} />
          </div>
          
          <div className="bg-black/30 backdrop-blur-3xl p-2 rounded-[2rem] border border-white/10 flex items-center gap-3 shadow-2xl">
             <button onClick={onMonthView} className={`px-10 py-4 rounded-[1.5rem] text-[10px] font-black uppercase tracking-[0.3em] transition-all duration-700 ${activeView === 'dayGridMonth' ? 'bg-emerald-400 text-[#05322B] shadow-2xl scale-105' : 'text-white/60 hover:text-white hover:bg-white/5'}`}>Mensuel</button>
             <button onClick={onWeekView} className={`px-10 py-4 rounded-[1.5rem] text-[10px] font-black uppercase tracking-[0.3em] transition-all duration-700 ${activeView === 'timeGridWeek' ? 'bg-emerald-400 text-[#05322B] shadow-2xl scale-105' : 'text-white/60 hover:text-white hover:bg-white/5'}`}>Hebdomadaire</button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

function renderCompactEventContent(eventInfo: EventContentArg) {
  const startTime = moment(eventInfo.event.start).format("HH:mm");
  return (
    <div className="group/chip relative flex flex-col gap-1 w-full p-2.5 rounded-2xl border border-black/[0.03] shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden" 
         style={{ background: eventInfo.event.backgroundColor?.replace(/0\.3\)/, '0.1)') || 'rgba(0,0,0,0.03)' }}>
      <div className="absolute left-0 top-0 bottom-0 w-1 rounded-full opacity-60 group-hover/chip:w-1.5 transition-all" style={{ background: eventInfo.event.backgroundColor?.replace(/0\.3\)/, '0.8)') || '#0369A1' }} />
      <div className="flex items-center gap-1.5 whitespace-nowrap overflow-hidden"><span className="w-1.5 h-1.5 rounded-full shadow-inner animate-pulse flex-shrink-0" style={{ background: eventInfo.event.backgroundColor?.replace(/0\.3\)/, '0.9)') || '#0369A1' }} /><span className="text-[9px] font-black text-text/40 tracking-tighter uppercase">{startTime}</span></div>
      <span className="text-[10px] font-black text-title leading-none truncate tracking-tight group-hover/chip:text-primary transition-colors">{eventInfo.event.title}</span>
    </div>
  );
}

function renderEventContent(eventInfo: EventContentArg) {
  return (
    <div className="group/event relative flex flex-col justify-between p-4 h-full transition-all duration-300 hover:scale-[1.02] cursor-pointer overflow-hidden border border-white/40 shadow-2xl rounded-2xl" style={{ background: `linear-gradient(135deg, ${eventInfo.event.backgroundColor?.replace(/0\.3\)/, '0.95)') || "#09745F"} 0%, ${eventInfo.event.backgroundColor?.replace(/0\.3\)/, '0.8)') || "#07594A"} 100%)` }}>
      <div className="absolute inset-0 bg-gradient-to-tr from-white/10 to-transparent opacity-20 group-hover/event:opacity-40 transition-opacity translate-y-full group-hover/event:translate-y-0 duration-700" />
      <div className="relative z-10 flex flex-col h-full justify-between gap-2 text-white">
        <div className="flex items-center gap-2 bg-black/20 backdrop-blur-md rounded-lg px-2 py-1 w-fit border border-white/10"><span className="text-[9px] font-black uppercase tracking-widest">{moment(eventInfo.event.start).format("HH:mm")}—{moment(eventInfo.event.end).format("HH:mm")}</span></div>
        <h3 className="text-xs font-black leading-tight drop-shadow-md line-clamp-2">{eventInfo.event.title}</h3>
        {eventInfo.event.extendedProps.subjectTitle && <div className="text-[8px] font-black opacity-60 uppercase tracking-[0.2em] truncate">{eventInfo.event.extendedProps.subjectTitle}</div>}
      </div>
    </div>
  );
}

// Event List Item Component
const EventListItem: React.FC<{ event: CalendarEvent; onClick: () => void; index: number }> = ({ event, onClick, index }) => {
  const startTime = moment(event.start).format("HH:mm");
  const endTime = moment(event.end).format("HH:mm");
  const eventDate = moment(event.start).format("ddd, D MMM");
  const isToday = moment(event.start).isSame(moment(), 'day');
  const isPast = moment(event.start).isBefore(moment(), 'day');

  return (
    <motion.div
      initial={{ x: 20, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ delay: index * 0.1 }}
      onClick={onClick}
      className={`group relative p-6 lg:p-7 rounded-[2.5rem] cursor-pointer transition-all duration-700 hover:shadow-[0_40px_80px_-20px_rgba(5,50,43,0.1)] border border-white/50 mb-4 overflow-hidden ${isPast ? 'opacity-50 grayscale-[0.5]' : 'bg-white/80 backdrop-blur-xl shadow-[0_20px_40px_-10px_rgba(0,0,0,0.02)] hover:-translate-y-1.5'}`}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
      <div className="absolute top-0 left-0 w-2 h-full transition-all duration-700 group-hover:w-3" style={{ background: event.backgroundColor?.replace(/0\.3\)/, '1)') || "#0369A1" }} />
      
      <div className="flex items-start justify-between relative z-10">
        <div className="space-y-4 flex-1 lg:pr-10">
           <div className="flex items-center gap-3">
              <div className={`px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-[0.2em] shadow-sm ${isToday ? 'bg-[#05322B] text-emerald-400 border border-emerald-900/10' : 'bg-gray-100/50 text-text/30'}`}>
                 {eventDate}
              </div>
              {isToday && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-full border border-emerald-100">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping"></span>
                  <span className="text-[9px] font-black text-emerald-600 uppercase tracking-widest">En Direct</span>
                </div>
              )}
           </div>
           
           <h3 className="text-[18px] lg:text-[22px] font-black text-title leading-[1.1] tracking-tighter group-hover:text-primary transition-all duration-500 max-w-[90%]">{event.title}</h3>
           
           <div className="flex flex-wrap items-center gap-2 pt-2">
              <div className="inline-flex items-center gap-2.5 px-3 py-1.5 bg-white border border-black/[0.03] rounded-[1rem] group-hover:bg-white group-hover:border-primary/20 transition-all shadow-sm">
                 <div className="w-1.5 h-1.5 rounded-full" style={{ background: event.backgroundColor?.replace(/0\.3\)/, '1)') || "#0369A1" }}></div>
                 <span className="text-[10px] font-black text-title/60 uppercase tracking-widest truncate max-w-[120px]">{event.classTitle}</span>
              </div>
              {event.subjectTitle && (
                <div className="px-3 py-1.5 bg-emerald-50/50 rounded-[1rem] border border-emerald-100/50">
                   <span className="text-[10px] font-black text-emerald-700 uppercase tracking-widest leading-none truncate max-w-[150px]">{event.subjectTitle}</span>
                </div>
              )}
           </div>
        </div>
        
        <div className="flex flex-col items-end gap-3 shrink-0">
           <div className="px-4 py-3 bg-white/40 backdrop-blur-xl border border-white rounded-[1.5rem] shadow-sm flex flex-col items-end group-hover:shadow-lg transition-all border-t-white/80">
              <span className="text-[8px] font-black text-text/30 uppercase tracking-[0.2em] mb-1">Session</span>
              <span className="text-[13px] lg:text-base font-black text-title uppercase tracking-tighter whitespace-nowrap">{startTime} — {endTime}</span>
           </div>
           <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-2xl bg-primary/5 flex items-center justify-center text-primary opacity-0 transform translate-x-5 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500 group-hover:bg-[#05322B] group-hover:text-emerald-400 shadow-xl shadow-primary/10">
              <ArrowRight fontSize="medium" />
           </div>
        </div>
      </div>
    </motion.div>
  );
};

const Calendar: React.FC = () => {
  const calendarRef = useRef<FullCalendar>(null);
  const globalCalendarRef = useRef<FullCalendar>(null);
  const [currentDate, setCurrentDate] = useState(moment().format("MMMM, YYYY"));
  const [activeView, setActiveView] = useState("dayGridMonth");
  const [groupOptions, setGroupOptions] = useState<GroupOption[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<GroupOption[]>([]);
  const [subjectOptionsByGroup, setSubjectOptionsByGroup] = useState<Record<number, SubjectOption[]>>({});
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [open, setOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedEventDetails, setSelectedEventDetails] = useState<CalendarEvent | null>(null);
  const [calendarModalOpen, setCalendarModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const userRole = useSelector((state: RootState) => state?.user?.userData?.role?.name);
  const canManageEvents = userRole === "ROLE_ADMIN" || userRole === "ROLE_SUPER_TEACHER";

  const [eventData, setEventData] = useState<EventFormState>({
    id: null,
    title: "",
    start: "",
    end: "",
    backgroundColor: colorOptions[0].rgba,
    classId: null,
    subjectId: null,
    discordUrl: "",
  });

  useEffect(() => {
    if (calendarModalOpen) {
      setTimeout(() => {
        globalCalendarRef.current?.getApi().updateSize();
      }, 500); 
    }
  }, [calendarModalOpen]);

  useEffect(() => {
    const bootstrap = async () => {
      try {
        setLoading(true);
        const res = await getUserGroupService();
        const options: GroupOption[] = res?.data?.map((group: any) => ({ label: group.title, value: group.id })) ?? [];
        setGroupOptions(options);
        setSelectedGroups(options);
        setEventData((prev) => ({ ...prev, classId: options[0]?.value ?? null }));
        await loadGroupEvents(options);
      } catch (e) {
        console.log(e);
      } finally {
        setLoading(false);
      }
    };
    bootstrap();
  }, []);

  const loadGroupEvents = async (groupsToLoad: GroupOption[]) => {
    if (!groupsToLoad.length) {
      setEvents([]);
      return;
    }
    const unwrap = (res: any) => res?.data?.data ?? res?.data ?? res;
    const results = await Promise.all(groupsToLoad.map(async (group) => {
      try {
        const [eventsRes, groupRes] = await Promise.all([getEventGroupService(group.value), getGroupByIdService(group.value)]);
        const groupData = unwrap(groupRes);
        const subjects: SubjectOption[] = groupData?.subjects?.map((s: any) => {
          const id = s?.id ?? null;
          if (id === null) return null;
          const label = `${s?.speciality ?? s?.title ?? s?.name ?? `Matiere ${id}`} (${s?.level ?? 'N/A'})`;
          return { id: Number(id), label };
        }).filter((s: SubjectOption | null): s is SubjectOption => Boolean(s)) ?? [];
        const eventDataList = unwrap(eventsRes);
        const mappedEvents: CalendarEvent[] = eventDataList?.map((e: any) => ({
          id: String(e.id),
          title: e.title,
          start: e.startTime,
          end: e.endTime,
          backgroundColor: e.backgroundColor,
          discordUrl: e.discordUrl,
          subjectId: e.subjectId ?? null,
          subjectTitle: subjects.find((s) => s.id === Number(e.subjectId))?.label ?? undefined,
          classId: group.value,
          classTitle: group.label,
          subjects: subjects.map((s) => s.label),
        })) ?? [];
        return { groupId: group.value, subjects, events: mappedEvents };
      } catch (error) {
        console.log(error);
        return { groupId: group.value, subjects: [], events: [] };
      }
    }));
    const subjectMap: Record<number, SubjectOption[]> = {};
    results.forEach((result) => { subjectMap[result.groupId] = result.subjects; });
    setSubjectOptionsByGroup((prev) => ({ ...prev, ...subjectMap }));
    setEvents(results.flatMap((result) => result.events));
  };

  const displayedEvents = useMemo(() => {
    if (!selectedGroups.length) return events;
    const ids = selectedGroups.map((g) => g.value);
    return events.filter((evt) => ids.includes(evt.classId));
  }, [events, selectedGroups]);

  const selectedSubjects = useMemo(() => {
    const ids = selectedGroups.map((g) => g.value);
    const bag = new Set<string>();
    ids.forEach((id) => { (subjectOptionsByGroup[id] ?? []).forEach((s) => bag.add(s.label)); });
    return Array.from(bag);
  }, [selectedGroups, subjectOptionsByGroup]);

  const handlePrevClick = () => { if (calendarRef.current) { const api = calendarRef.current.getApi(); api.prev(); updateCurrentDate(api.getDate()); } };
  const handleNextClick = () => { if (calendarRef.current) { const api = calendarRef.current.getApi(); api.next(); updateCurrentDate(api.getDate()); } };
  const handleTodayClick = () => { if (calendarRef.current) { const api = calendarRef.current.getApi(); api.today(); updateCurrentDate(api.getDate()); } };
  const updateCurrentDate = (date: Date) => setCurrentDate(moment(date).format("MMMM, YYYY"));

  const handleOpen = () => {
    const defaultClassId = selectedGroups[0]?.value ?? groupOptions[0]?.value ?? null;
    const defaultSubjectId = (defaultClassId ? subjectOptionsByGroup[defaultClassId]?.[0]?.id : null) ?? null;
    setOpen(true);
    setIsEditMode(false);
    setEventData({ id: null, title: "", start: "", end: "", backgroundColor: colorOptions[0].rgba, classId: defaultClassId, subjectId: defaultSubjectId, discordUrl: "" });
  };

  const handleClose = () => setOpen(false);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setEventData({ ...eventData, [name as string]: value });
  };

  const handleSubmit = async () => {
    if (!eventData.title.trim() || !eventData.start || !eventData.end || !eventData.classId) return;
    const payload = { title: eventData.title, backgroundColor: eventData.backgroundColor, endTime: eventData.end, startTime: eventData.start, subjectId: eventData.subjectId, discordUrl: eventData.discordUrl };
    try {
      if (isEditMode && eventData.id) {
        const res = await updateEventGroupService(eventData.classId, Number(eventData.id), payload);
        const updated = res?.data;
        const classTitle = groupOptions.find((g) => g.value === eventData.classId)?.label ?? "";
        const subjectOptions = subjectOptionsByGroup[eventData.classId] ?? [];
        const subjectId = updated?.subjectId ?? eventData.subjectId ?? null;
        const subjectTitle = subjectOptions.find((s) => s.id === subjectId)?.label ?? undefined;
        setEvents((prev) => prev.map((evt) => evt.id === eventData.id ? { ...evt, title: updated?.title ?? eventData.title, start: updated?.startTime ?? eventData.start, end: updated?.endTime ?? eventData.end, backgroundColor: updated?.backgroundColor ?? eventData.backgroundColor, discordUrl: updated?.discordUrl ?? eventData.discordUrl, classId: eventData.classId!, classTitle, subjectId, subjectTitle } : evt));
      } else {
        const res = await addEventToGroupService(eventData.classId, payload);
        const created = res?.data;
        const classTitle = groupOptions.find((g) => g.value === eventData.classId)?.label ?? "";
        const subjectOptions = subjectOptionsByGroup[eventData.classId] ?? [];
        const subjectId = created?.subjectId ?? eventData.subjectId ?? null;
        const subjectTitle = subjectOptions.find((s) => s.id === subjectId)?.label ?? undefined;
        setEvents((prev) => [...prev, { id: String(created?.id ?? Date.now()), title: created?.title ?? eventData.title, start: created?.startTime ?? eventData.start, end: created?.endTime ?? eventData.end, backgroundColor: created?.backgroundColor ?? eventData.backgroundColor, discordUrl: created?.discordUrl ?? eventData.discordUrl, classId: eventData.classId!, classTitle, subjectId, subjectTitle }]);
      }
      handleClose();
    } catch (e) { console.log(e); }
  };

  const removeEventById = (eventId: string) => setEvents((prev) => prev.filter((evt) => evt.id !== eventId));

  const handleDelete = async () => {
    if (!eventData.classId || !eventData.id) return;
    if (!window.confirm("Supprimer cet événement ?")) return;
    try { await deleteEventGroupService(eventData.classId, Number(eventData.id)); removeEventById(eventData.id); handleClose(); } catch (e) { console.log(e); }
  };

  const handleDeleteFromDetails = async () => {
    if (!selectedEventDetails || !canManageEvents) return;
    if (!window.confirm("Supprimer cet événement ?")) return;
    try { await deleteEventGroupService(selectedEventDetails.classId, Number(selectedEventDetails.id)); removeEventById(selectedEventDetails.id); setDetailsModalOpen(false); } catch (e) { console.log(e); }
  };

  const handleEventClick = (info: { event: EventApi }) => {
    const fullEvent = events.find(evt => evt.id === String(info.event.id));
    if (fullEvent) { setSelectedEventDetails(fullEvent); setDetailsModalOpen(true); }
  };

  const handleEditFromDetails = () => {
    if (selectedEventDetails && canManageEvents) {
      setIsEditMode(true); setOpen(true); setDetailsModalOpen(false);
      setEventData({ id: selectedEventDetails.id, title: selectedEventDetails.title, start: moment(selectedEventDetails.start).format("YYYY-MM-DDTHH:mm"), end: moment(selectedEventDetails.end).format("YYYY-MM-DDTHH:mm"), backgroundColor: selectedEventDetails.backgroundColor || colorOptions[0].rgba, classId: selectedEventDetails.classId, subjectId: selectedEventDetails.subjectId ?? null, discordUrl: selectedEventDetails.discordUrl || "" });
    }
  };

  const handleCustomButtonClick = (viewType: string) => { if (calendarRef.current) { const api = calendarRef.current.getApi(); api.changeView(viewType); setActiveView(viewType); } };

  const handleClassChange = (event: SelectChangeEvent<number>) => {
    const val = Number(event.target.value);
    const id = Number.isNaN(val) ? null : val;
    setEventData((prev) => ({ ...prev, classId: id, subjectId: id != null ? subjectOptionsByGroup[id]?.[0]?.id ?? null : null }));
  };

  const handleSubjectChange = (event: SelectChangeEvent<number | string>) => {
    const val = Number(event.target.value);
    setEventData((prev) => ({ ...prev, subjectId: Number.isNaN(val) ? null : val }));
  };

  const eventsForSelectedDate = useMemo(() => {
    if (!selectedDate) return displayedEvents;
    const dateStr = moment(selectedDate).format('YYYY-MM-DD');
    return displayedEvents.filter(evt => moment(evt.start).format('YYYY-MM-DD') === dateStr);
  }, [selectedDate, displayedEvents]);

  const sortedEventsList = useMemo(() => [...displayedEvents].sort((a, b) => moment(a.start).valueOf() - moment(b.start).valueOf()), [displayedEvents]);

  return (
    <div className="relative w-full max-w-full space-y-6 p-4 lg:p-8 min-h-screen">
      <div className="absolute inset-0 pointer-events-none -z-10 overflow-hidden">
        <div className="absolute top-[-20%] left-[-5%] w-[60%] h-[70%] bg-primary/3 blur-[120px] rounded-full mix-blend-multiply opacity-60 animate-pulse" />
        <div className="absolute bottom-[0%] right-[-10%] w-[50%] h-[60%] bg-[#0af0b4]/2 blur-[100px] rounded-full mix-blend-screen opacity-50" />
      </div>

      <Header currentDate={currentDate} onPrev={handlePrevClick} onNext={handleNextClick} onToday={handleTodayClick} onWeekView={() => handleCustomButtonClick("timeGridWeek")} onMonthView={() => handleCustomButtonClick("dayGridMonth")} activeView={activeView} onOpenModal={handleOpen} groupOptions={groupOptions} selectedGroups={selectedGroups} onGroupsChange={setSelectedGroups} subjectTags={selectedSubjects} canAddEvents={canManageEvents} loading={loading} totalEvents={displayedEvents.length} />

      <div className="grid grid-cols-1 gap-8 xl:grid-cols-12">
        <div className="relative xl:col-span-7">
          <div className="relative p-6 border border-black/[0.03] shadow-[0_40px_100px_-20px_rgba(0,0,0,0.06)] bg-white/70 backdrop-blur-3xl rounded-[2.5rem] transition-all duration-700 hover:shadow-[0_60px_120px_-30px_rgba(9,116,95,0.12)] border-t-white/80 h-full">
            <div className="flex items-center justify-between mb-6">
              <div className="space-y-1">
                 <h2 className="text-3xl font-black text-title flex items-center gap-4">📅 Calendrier</h2>
                 <p className="text-[10px] font-black text-text/30 uppercase tracking-[0.2em]">Cliquer sur une date pour filtrer</p>
              </div>
              <button onClick={() => setCalendarModalOpen(true)} className="group/btn relative px-10 py-4 bg-white text-primary rounded-[1.5rem] font-black text-[11px] uppercase tracking-widest shadow-[0_20px_40px_-10px_rgba(0,0,0,0.05)] border border-black/[0.03] hover:text-white transition-all duration-700 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-primary to-[#0A9C82] opacity-0 group-hover/btn:opacity-100 transition-opacity translate-y-full group-hover/btn:translate-y-0 duration-700"></div>
                <div className="relative z-10 flex items-center gap-2">
                  <span>Agrandir</span>
                  <svg className="w-3.5 h-3.5 transform group-hover/btn:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                </div>
              </button>
            </div>
            <div className="calendar-compact">
              <FullCalendar ref={calendarRef} plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]} initialView="dayGridMonth" events={displayedEvents} height={"auto"} aspectRatio={1.3} eventClick={handleEventClick} dateClick={(info) => setSelectedDate(info.date)} eventContent={renderCompactEventContent} headerToolbar={false} eventDisplay="block" dayMaxEvents={2} />
            </div>
          </div>
        </div>

        <div className="relative xl:col-span-5 h-full">
          <div className="relative p-8 border border-black/5 shadow-[0_50px_100px_-30px_rgba(0,0,0,0.1)] bg-white/90 backdrop-blur-3xl rounded-[3rem] flex flex-col h-full border-t-white/80">
            <div className="flex flex-col items-start justify-between gap-4 mb-6 sm:flex-row sm:items-center">
              <div>
                <h2 className="text-3xl font-black text-title flex items-center gap-4">📋 Événements {selectedDate ? <span className="w-2.5 h-2.5 rounded-full bg-primary animate-pulse" /> : <span className="text-[11px] font-black text-white bg-primary px-3 py-1.5 rounded-full shadow-lg shadow-primary/20">{displayedEvents.length}</span>}</h2>
                {selectedDate && <p className="text-[10px] font-black text-primary uppercase tracking-[0.2em] mt-2 bg-primary/5 px-3 py-1 rounded-full w-fit">{moment(selectedDate).format('D MMMM YYYY')}</p>}
              </div>
              {selectedDate && <button onClick={() => setSelectedDate(null)} className="px-6 py-3 text-[10px] font-black text-primary uppercase tracking-widest bg-primary/5 border border-primary/10 rounded-2xl hover:bg-primary hover:text-white transition-all">Tout voir</button>}
            </div>
            <div className="flex-1 overflow-y-auto pr-2 -mr-6 custom-scrollbar pb-8">
              {(selectedDate ? eventsForSelectedDate : sortedEventsList).length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 text-center opacity-40">
                   <div className="w-24 h-24 mb-8 rounded-full bg-gray-50 flex items-center justify-center shadow-inner animate-pulse"><span className="text-5xl">🏷️</span></div>
                   <h4 className="text-xl font-black text-title">Aucun événement</h4>
                </div>
              ) : (
                <AnimatePresence mode="popLayout">
                  {(selectedDate ? eventsForSelectedDate : sortedEventsList).map((event, i) => (
                    <EventListItem 
                      key={event.id} 
                      event={event} 
                      index={i}
                      onClick={() => { setSelectedEventDetails(event); setDetailsModalOpen(true); }} 
                    />
                  ))}
                </AnimatePresence>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal 
        open={calendarModalOpen} 
        onClose={() => setCalendarModalOpen(false)}
        className="flex items-center justify-center p-4 backdrop-blur-2xl bg-title/40"
      >
        <motion.div 
          className="w-full max-w-[1300px] h-[90vh] bg-white rounded-[4rem] p-10 shadow-3xl overflow-hidden flex flex-col outline-none relative"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
        >
           <div className="absolute inset-0 pointer-events-none opacity-20 overflow-hidden">
             <div className="absolute -top-24 -left-24 w-96 h-96 bg-primary/20 blur-[100px] rounded-full" />
             <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-[#0af0b4]/20 blur-[100px] rounded-full" />
           </div>

           <div className="flex flex-col gap-8 md:flex-row md:items-center md:justify-between mb-8 px-4 relative z-10">
              <h2 className="text-[2.5rem] font-black text-[#05322B] tracking-tighter">Calendrier <span className="text-primary italic">Interactif</span></h2>
              
              <div className="flex items-center gap-4 bg-gray-50 border border-black/[0.03] p-2 rounded-[2rem]">
                 <IconButton onClick={() => globalCalendarRef.current?.getApi().prev()}><ArrowLeft /></IconButton>
                 <button onClick={() => globalCalendarRef.current?.getApi().today()} className="px-6 py-2 text-[10px] font-black uppercase tracking-widest text-[#05322B] hover:bg-white rounded-full transition-all">Aujourd'hui</button>
                 <IconButton onClick={() => globalCalendarRef.current?.getApi().next()}><ArrowRight /></IconButton>
              </div>

              <div className="flex items-center gap-2">
                 {['dayGridMonth', 'timeGridWeek', 'listWeek'].map((view) => (
                    <button 
                      key={view}
                      onClick={() => globalCalendarRef.current?.getApi().changeView(view)}
                      className={`px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${globalCalendarRef.current?.getApi().view.type === view ? 'bg-primary text-white shadow-lg' : 'bg-gray-50 text-text/40 hover:bg-gray-100'}`}
                    >
                       {view === 'dayGridMonth' ? 'Mois' : view === 'timeGridWeek' ? 'Semaine' : 'Liste'}
                    </button>
                 ))}
                 <button 
                    onClick={() => setCalendarModalOpen(false)}
                    className="ml-4 w-12 h-12 rounded-full bg-rose-50 text-rose-500 flex items-center justify-center hover:bg-rose-500 hover:text-white transition-all duration-500"
                  >
                     <FontAwesomeIcon icon={faPlus} className="rotate-45" />
                  </button>
              </div>
           </div>

           <div className="flex-1 overflow-hidden px-4 pb-4 relative z-10">
              <div className="h-full rounded-[2.5rem] overflow-hidden border border-black/[0.03] shadow-inner bg-white/50 backdrop-blur-sm">
                <FullCalendar 
                  ref={globalCalendarRef}
                  plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]} 
                  initialView="dayGridMonth" 
                  events={displayedEvents} 
                  height={"100%"} 
                  eventClick={handleEventClick} 
                  eventContent={renderEventContent} 
                  headerToolbar={false}
                  eventDisplay="block" 
                />
              </div>
           </div>
        </motion.div>
      </Modal>

      {/* Modal Créer/Éditer Événement (Drawer) */}
      <Modal 
        open={open} 
        onClose={handleClose}
        className="flex justify-end backdrop-blur-sm bg-title/20"
        closeAfterTransition
      >
        <div className={`h-full w-full max-w-[550px] bg-white shadow-[-20px_0_80px_rgba(0,0,0,0.1)] outline-none transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] flex flex-col ${open ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="p-8 pb-6 flex items-center justify-between border-b border-gray-50 bg-white/50 backdrop-blur-md flex-shrink-0 z-30">
            <div>
              <h2 className="text-2xl font-black text-title uppercase tracking-tighter">{isEditMode ? "Édition" : "Nouveau"} <span className="text-primary italic">Cours</span></h2>
              <p className="text-[9px] font-black text-primary uppercase tracking-[0.4em] mt-1 italic">Configuration de la session mobile</p>
            </div>
            <button onClick={handleClose} className="w-12 h-12 flex items-center justify-center rounded-2xl bg-gray-50 text-text/30 hover:bg-rose-50 hover:text-rose-500 transition-all duration-300">
              <FontAwesomeIcon icon={faTimes} className="text-lg transition-transform duration-500" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto no-scrollbar p-8 space-y-10 bg-white">
            <section className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-3">
                  <label className="text-[11px] font-black text-title uppercase tracking-widest flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-primary" />Cible (Groupe)</label>
                  <Select fullWidth name="classId" value={eventData.classId ?? ""} onChange={handleClassChange} sx={{ borderRadius: "1.25rem", height: 60, bgcolor: '#f8fafc', border: 'none', "& .MuiOutlinedInput-notchedOutline": { border: 'none' }, fontWeight: 900, textTransform: 'uppercase', fontSize: 11 }}>
                    {groupOptions.map((g) => <MenuItem key={g.value} value={g.value} sx={{ fontWeight: 800 }}>{g.label}</MenuItem>)}
                  </Select>
                </div>
                <div className="space-y-3">
                  <label className="text-[11px] font-black text-title uppercase tracking-widest flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-primary" />Matière</label>
                  <Select fullWidth name="subjectId" value={eventData.subjectId ?? ""} onChange={handleSubjectChange} sx={{ borderRadius: "1.25rem", height: 60, bgcolor: '#f8fafc', border: 'none', "& .MuiOutlinedInput-notchedOutline": { border: 'none' }, fontWeight: 900, textTransform: 'uppercase', fontSize: 11 }}>
                    <MenuItem value="" sx={{ fontWeight: 800 }}><em>Groupe complet</em></MenuItem>
                    {(eventData.classId != null ? subjectOptionsByGroup[eventData.classId] ?? [] : []).map((s) => <MenuItem key={s.id} value={s.id} sx={{ fontWeight: 800 }}>{s.label}</MenuItem>)}
                  </Select>
                </div>
              </div>
              <div className="space-y-3">
                <label className="text-[11px] font-black text-title uppercase tracking-widest flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-primary" />Titre de la session</label>
                <input className="w-full h-14 bg-slate-50 border-slate-100 rounded-2xl px-6 font-black text-title text-base outline-none border focus:bg-white transition-all shadow-sm" name="title" value={eventData.title} onChange={handleChange} placeholder="Ex: Révision Algorithme" />
              </div>
            </section>
            <section className="space-y-6">
              <h3 className="text-[11px] font-black text-title uppercase tracking-widest flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#0af0b4]" />Planification Horaires</h3>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-3">
                  <label className="text-[10px] font-bold text-text/40 uppercase tracking-widest ml-1">Début</label>
                  <input type="datetime-local" className="w-full h-14 bg-slate-50 border-slate-100 rounded-2xl px-6 font-black text-title text-xs outline-none border" name="start" value={eventData.start} onChange={handleChange} />
                </div>
                <div className="space-y-3">
                  <label className="text-[10px] font-bold text-text/40 uppercase tracking-widest ml-1">Fin</label>
                  <input type="datetime-local" className="w-full h-14 bg-slate-50 border-slate-100 rounded-2xl px-6 font-black text-title text-xs outline-none border" name="end" value={eventData.end} onChange={handleChange} />
                </div>
              </div>
            </section>
            <section className="space-y-6">
              <h3 className="text-[11px] font-black text-title uppercase tracking-widest flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-blue-500" />Salon Virtuel</h3>
              <div className="space-y-3">
                <label className="text-[10px] font-bold text-text/40 uppercase tracking-widest ml-1">Lien de réunion</label>
                <input className="w-full h-14 bg-slate-50 border-slate-100 rounded-2xl px-6 font-black text-title text-sm outline-none border" name="discordUrl" value={eventData.discordUrl} onChange={handleChange} placeholder="https://discord.gg/..." />
              </div>
            </section>
          </div>
          <div className="p-8 bg-white border-t border-gray-100 flex flex-wrap gap-4 z-40">
            <button onClick={handleClose} className="px-8 h-14 rounded-2xl font-black text-[11px] uppercase tracking-widest text-text/40 hover:text-rose-500 transition-all">Annuler</button>
            {isEditMode && <button onClick={handleDelete} className="px-8 h-14 bg-rose-50 text-rose-500 rounded-2xl font-black text-[11px] uppercase tracking-widest hover:bg-rose-500 hover:text-white transition-all">Supprimer</button>}
            <button onClick={handleSubmit} className="flex-1 h-14 bg-[#05322B] text-white rounded-2xl font-black text-[11px] uppercase tracking-[0.3em] shadow-xl flex items-center justify-center gap-4 group">
              <span>{isEditMode ? "Sauvegarder" : "Confirmer"}</span>
              <FontAwesomeIcon icon={faCheck} className="text-xs group-hover:rotate-12 transition-transform" />
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal Détails Événement (Drawer) */}
      <Modal 
        open={detailsModalOpen} 
        onClose={() => setDetailsModalOpen(false)}
        className="flex justify-end backdrop-blur-sm bg-title/20"
        closeAfterTransition
      >
        <div className={`h-full w-full max-w-[550px] bg-white shadow-[-20px_0_80px_rgba(0,0,0,0.1)] outline-none transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] flex flex-col ${detailsModalOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="h-2 bg-gradient-to-r from-primary via-[#0af0b4] to-primary z-50 flex-shrink-0" />
          <div className="p-8 pb-6 flex items-center justify-between border-b border-gray-50 flex-shrink-0 relative z-30">
            <div className="flex items-center gap-5 focus:outline-none">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center text-3xl shadow-inner border border-primary/5">🎯</div>
              <div>
                <h2 className="text-xl font-black text-title uppercase tracking-tighter leading-none line-clamp-1">{selectedEventDetails?.title}</h2>
                <div className="flex items-center gap-2 mt-2">
                  <span className="px-3 py-1 bg-[#05322B] text-emerald-400 rounded-full text-[9px] font-black uppercase tracking-widest">{selectedEventDetails?.classTitle}</span>
                  {selectedEventDetails && moment(selectedEventDetails.start).isSame(moment(), 'day') && (
                    <span className="px-2 py-1 bg-emerald-50 text-emerald-600 rounded-full text-[8px] font-black uppercase tracking-widest border border-emerald-100 flex items-center gap-1">
                      <span className="w-1 h-1 rounded-full bg-emerald-500 animate-ping" /> Live
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button onClick={() => setDetailsModalOpen(false)} className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-50 text-text/30 hover:bg-rose-50 hover:text-rose-500 transition-all focus:outline-none">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto no-scrollbar p-8 space-y-8 bg-white focus:outline-none">
            {selectedEventDetails && (
              <>
                <div className="grid grid-cols-1 gap-4">
                  <div className="p-6 rounded-3xl bg-slate-50 border border-slate-100 space-y-4 hover:shadow-xl transition-all group">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-[#05322B] shadow-sm group-hover:scale-110 transition-transform"><FontAwesomeIcon icon={faCalendarAlt} /></div>
                      <span className="text-[10px] font-black text-text/30 uppercase tracking-[0.2em]">Planning</span>
                    </div>
                    <div>
                      <p className="text-xl font-black text-title">{moment(selectedEventDetails.start).format("DD MMMM YYYY")}</p>
                      <p className="text-2xl font-black text-primary mt-1">{moment(selectedEventDetails.start).format("HH:mm")} — {moment(selectedEventDetails.end).format("HH:mm")}</p>
                    </div>
                  </div>
                  <div className="p-6 rounded-3xl bg-slate-50 border border-slate-100 space-y-4 hover:shadow-xl transition-all group">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-[#05322B] shadow-sm group-hover:scale-110 transition-transform"><FontAwesomeIcon icon={faBook} /></div>
                      <span className="text-[10px] font-black text-text/30 uppercase tracking-[0.2em]">Module</span>
                    </div>
                    <div>
                      <p className="text-xl font-black text-title">{selectedEventDetails.subjectTitle || "Session Groupe"}</p>
                    </div>
                  </div>
                </div>
                {selectedEventDetails.discordUrl && (
                  <div onClick={() => window.open(selectedEventDetails.discordUrl, '_blank')} className="p-8 rounded-[2.5rem] bg-[#5865F2] text-white flex items-center justify-between group cursor-pointer shadow-xl hover:scale-[1.02] transition-all">
                    <div className="flex items-center gap-6">
                      <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center text-white backdrop-blur-xl group-hover:rotate-6 transition-transform"><FontAwesomeIcon icon={faLink} className="text-2xl" /></div>
                      <div>
                        <p className="text-xl font-black tracking-tighter uppercase leading-none">Rejoindre Salon</p>
                        <p className="text-[9px] font-black text-white/50 uppercase tracking-widest mt-1 italic">Discord</p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="p-8 bg-white border-t border-gray-100 flex gap-4 z-40">
            {canManageEvents ? (
              <>
                <button onClick={handleDeleteFromDetails} className="flex-1 h-14 bg-rose-50 text-rose-500 rounded-2xl font-black text-[11px] uppercase tracking-widest hover:bg-rose-500 hover:text-white transition-all">Supprimer</button>
                <button onClick={handleEditFromDetails} className="flex-[2] h-14 bg-[#05322B] text-white rounded-2xl font-black text-[11px] uppercase tracking-widest shadow-xl transition-all">Modifier</button>
              </>
            ) : (
              <button onClick={() => setDetailsModalOpen(false)} className="w-full h-14 bg-gray-50 text-text/40 rounded-2xl font-black text-[11px] uppercase tracking-widest hover:bg-gray-100 transition-all">Fermer</button>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};
export default Calendar;

// Add Global Styles for calendar wow factor
const injectCalendarStyles = () => {
  if (typeof document === 'undefined') return;
  const id = 'calendar-wow-styles';
  if (document.getElementById(id)) return;
  const style = document.createElement('style');
  style.id = id;
  style.textContent = `
    .fc { font-family: 'Outfit', sans-serif !important; border: none !important; }
    .fc-theme-standard td, .fc-theme-standard th { border: 1px solid rgba(0,0,0,0.03) !important; }
    .fc-col-header-cell { background: rgba(0,0,0,0.02) !important; padding: 20px 0 !important; border: none !important; }
    .fc-col-header-cell-cushion { color: #05322B !important; font-weight: 900 !important; text-transform: uppercase !important; font-size: 11px !important; letter-spacing: 0.1em !important; }
    .fc-daygrid-day-number { font-weight: 900 !important; color: rgba(5,50,43,0.3) !important; font-size: 12px !important; padding: 15px !important; }
    .fc-day-today { background: rgba(9,116,95,0.03) !important; }
    .fc-day-today .fc-daygrid-day-number { color: #09745F !important; font-size: 16px !important; }
    .fc-event { border: none !important; background: transparent !important; }
    .calendar-compact .fc-col-header-cell { padding: 10px 0 !important; background: #09745F !important; }
    .calendar-compact .fc-col-header-cell-cushion { color: white !important; font-size: 9px !important; }
  `;
  document.head.appendChild(style);
};
injectCalendarStyles();
